import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gift, Loader2 } from "lucide-react";

const B = process.env.REACT_APP_BACKEND_URL;

export default function PublicRedeem({ stationId, accent, inputCls }) {
  const [items, setItems] = useState(null);
  const [email, setEmail] = useState("");
  const [bal, setBal] = useState(null);
  const [busy, setBusy] = useState("");
  const [requested, setRequested] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${B}/api/engagement/public/${stationId}/rewards-catalog`)
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [stationId]);

  const check = async () => {
    setBusy("bal"); setErr("");
    try {
      const r = await axios.post(`${B}/api/engagement/public/${stationId}/rewards/balance`, { email });
      setBal(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Couldn't check your points"); }
    setBusy("");
  };

  const redeem = async (item) => {
    setBusy(item.item_id); setErr("");
    try {
      const r = await axios.post(`${B}/api/engagement/public/${stationId}/rewards/redeem`,
        { email, item_id: item.item_id, note: "" });
      setRequested((p) => ({ ...p, [item.item_id]: true }));
      setBal((b) => (b ? { ...b, available: r.data.available, held: (b.held || 0) + item.cost_points } : b));
    } catch (e) { setErr(e?.response?.data?.detail || "Redeem failed"); }
    setBusy("");
  };

  if (!items) return <div className="py-10 text-center text-white/40"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="engage-redeem">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
        <div className="text-sm font-bold flex items-center gap-2"><Gift className="h-4 w-4" style={{ color: accent }} /> Spend your listener points</div>
        <div className="flex gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email you used on the show" data-testid="engage-redeem-email" className={inputCls} />
          <button onClick={check} disabled={busy === "bal" || !email} data-testid="engage-redeem-check-btn"
            className="rounded-md px-4 text-xs font-bold text-black shrink-0 disabled:opacity-60" style={{ background: accent }}>
            {busy === "bal" ? "…" : "Check points"}
          </button>
        </div>
        {bal && bal.found && (
          <div className="text-sm" data-testid="engage-redeem-balance">
            {bal.name ? `Hi ${bal.name} — ` : ""}you have <span className="font-black" style={{ color: accent }}>{bal.available}</span> points available
            {bal.held > 0 && <span className="text-white/40 text-xs"> ({bal.held} on hold pending confirmation)</span>}
          </div>
        )}
        {bal && !bal.found && (
          <div className="text-xs text-white/50" data-testid="engage-redeem-notfound">No points yet for that email — request a song or join a poll to start earning!</div>
        )}
      </div>

      {!items.length && <div className="rounded-xl border border-white/10 bg-white/[0.03] p-8 text-center text-sm text-white/50" data-testid="engage-redeem-empty">No rewards available right now — check back soon!</div>}
      {items.map((it) => (
        <div key={it.item_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 flex items-center gap-4" data-testid={`engage-reward-${it.item_id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold">{it.title}</div>
            {it.description && <div className="text-xs text-white/50 mt-0.5">{it.description}</div>}
            {!it.in_stock && <div className="text-[10px] text-rose-300 mt-1">Out of stock</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="font-black text-lg" style={{ color: accent }}>{it.cost_points} <span className="text-[10px] font-mono text-white/40">PTS</span></div>
            {requested[it.item_id] ? (
              <div className="text-[11px] text-emerald-300 font-bold mt-1" data-testid={`engage-redeemed-${it.item_id}`}>Request sent! 🎉</div>
            ) : (
              <button onClick={() => redeem(it)} data-testid={`engage-redeem-btn-${it.item_id}`}
                disabled={!bal?.found || !it.in_stock || busy === it.item_id || (bal?.available ?? 0) < it.cost_points}
                className="mt-1 rounded-full px-4 py-1.5 text-xs font-bold text-black disabled:opacity-40" style={{ background: accent }}>
                {busy === it.item_id ? "…" : "Redeem"}
              </button>
            )}
          </div>
        </div>
      ))}
      {err && <div className="text-xs text-rose-300" data-testid="engage-redeem-error">{err}</div>}
      <div className="text-[10px] text-white/30 text-center">The station confirms every redemption — points are only deducted once approved.</div>
    </div>
  );
}
